import { inject } from '@angular/core'
import { Router, Routes } from '@angular/router'
import { registrationResolver } from './auth/resolvers/registration.resolver'
import { authGuard, isOwnerGuard } from './auth/auth.guard'
import {
  clientsLegacySelectionGuard,
  eventsLegacySelectionGuard,
  reservationsLegacySelectionGuard,
} from './premises/legacy-selection.guard'
import { LoadFailedComponent } from './shared/components/load-failed/load-failed.component'
import { LOAD_FAILED_PATH } from './shared/services/stale-chunk-fallback'
import {
  legacyPublicPageMatcher,
  legacyPublicPageRedirect,
  publicPageMatcher,
} from './premises-calendar/public-page-route'
// import { UiKitComponent } from './ui-kit/ui-kit.component'

export const routes: Routes = [
  // {
  //   path: 'ui-kit',
  //   component: UiKitComponent,
  // },
  {
    path: '',
    canActivate: [authGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./signed-in/signed-in.component').then((c) => c.SignedInComponent),
        children: [
          {
            path: '',
            canActivate: [isOwnerGuard],
            children: [
              {
                path: '',
                redirectTo: 'dashboard',
                pathMatch: 'full',
              },
              {
                path: 'dashboard',
                loadComponent: () =>
                  import('./dashboard/pages/dashboard/dashboard.component').then((c) => c.DashboardComponent),
              },
              {
                path: 'notifications',
                loadComponent: () =>
                  import('./dashboard/pages/notifications/notifications.component').then(
                    (c) => c.NotificationsComponent,
                  ),
              },
              {
                path: 'user',
                loadComponent: () => import('./dashboard/pages/user/user.component').then((c) => c.UserComponent),
              },
            ],
          },
          {
            path: 'premise',
            canActivate: [isOwnerGuard],
            children: [
              {
                path: 'employees',
                loadComponent: () =>
                  import('./premises/pages/employees/employees.component').then((c) => c.EmployeesComponent),
              },
              {
                path: 'employee/:id',
                loadComponent: () =>
                  import('./premises/pages/employee/employee.component').then((c) => c.EmployeeComponent),
              },
              {
                path: 'clients',
                canActivate: [clientsLegacySelectionGuard],
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                loadComponent: () =>
                  import('./premises/pages/customers/customers.component').then((c) => c.CustomersComponent),
              },
              {
                path: 'clients/:customerId',
                loadComponent: () =>
                  import('./premises/pages/client-detail/client-detail.component').then((c) => c.ClientDetailComponent),
              },
              { path: 'client/:id', redirectTo: 'clients/:id' },
              {
                path: 'activities',
                loadComponent: () =>
                  import('./premises/pages/activities/activities.component').then((c) => c.ActivitiesComponent),
              },
              {
                path: 'activity/:id',
                loadComponent: () =>
                  import('./premises/pages/activity/activity.component').then((c) => c.ActivityComponent),
              },
              {
                path: 'places',
                loadComponent: () => import('./premises/pages/places/places.component').then((c) => c.PlacesComponent),
              },
              {
                path: 'place/:id',
                loadComponent: () => import('./premises/pages/place/place.component').then((c) => c.PlaceComponent),
              },
              {
                path: 'events',
                canActivate: [eventsLegacySelectionGuard],
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                loadComponent: () => import('./premises/pages/events/events.component').then((c) => c.EventsComponent),
              },
              {
                path: 'events/:eventId',
                loadComponent: () =>
                  import('./premises/pages/event-detail/event-detail.component').then((c) => c.EventDetailComponent),
              },
              { path: 'event/:id', redirectTo: 'events/:id' },
              {
                path: 'reservations',
                canActivate: [reservationsLegacySelectionGuard],
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                loadComponent: () =>
                  import('./premises/pages/reservations/reservations.component').then((c) => c.ReservationsComponent),
              },
              {
                path: 'reservations/:reservationId',
                loadComponent: () =>
                  import('./premises/pages/reservation-detail/reservation-detail.component').then(
                    (c) => c.ReservationDetailComponent,
                  ),
              },
              {
                path: 'reservation-schemas',
                loadComponent: () =>
                  import('./premises/pages/blueprints/blueprints.component').then((c) => c.BlueprintsComponent),
              },
              {
                path: 'calendar',
                loadComponent: () =>
                  import('./premises/pages/calendar-events/calendar-events.component').then(
                    (c) => c.CalendarEventsComponent,
                  ),
              },
              {
                path: 'groups',
                loadComponent: () =>
                  import('./premises/pages/customer-groups/customer-groups.component').then(
                    (c) => c.CustomerGroupsComponent,
                  ),
              },
              {
                path: 'group/:id',
                loadComponent: () =>
                  import('./premises/pages/customer-group/customer-group.component').then(
                    (c) => c.CustomerGroupComponent,
                  ),
              },
              {
                path: 'payments',
                loadComponent: () =>
                  import('./premises/pages/payments/payments.component').then((c) => c.PaymentsComponent),
              },
              {
                path: 'account',
                loadComponent: () =>
                  import('./premises/pages/account/account.component').then((c) => c.AccountComponent),
              },
              {
                path: 'account-testing',
                loadComponent: () =>
                  import('./premises/pages/account-testing/account-testing.component').then(
                    (c) => c.AccountTestingComponent,
                  ),
              },
              {
                path: 'edit',
                loadComponent: () =>
                  import('./premises/pages/edit-premise/edit-premise.component').then((c) => c.EditPremiseComponent),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'account',
    children: [
      {
        path: '',
        loadComponent: () => import('./auth/auth.component').then((c) => c.AuthComponent),
        children: [
          {
            path: 'sign-in',
            loadComponent: () => import('./auth/pages/login/login.component').then((c) => c.LoginComponent),
          },
          {
            path: 'sign-up',
            loadComponent: () => import('./auth/pages/login/login.component').then((c) => c.LoginComponent),
            resolve: {
              inviteData: registrationResolver,
            },
          },
          {
            path: 'forgotten-password',
            loadComponent: () =>
              import('./auth/pages/forgot-password/forgot-password.component').then((c) => c.ForgotPasswordComponent),
          },
          {
            path: 'register',
            loadComponent: () =>
              import('./auth/pages/registration/registration.component').then((c) => c.RegistrationComponent),
          },
          {
            path: 'new-password',
            loadComponent: () =>
              import('./auth/pages/set-password/set-password.component').then((c) => c.SetPasswordComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'event-participation/:id',
    loadComponent: () =>
      import('./public/event-participations/pages/public-event-participation-detail.component').then(
        (c) => c.PublicEventParticipationDetailComponent,
      ),
  },
  {
    path: 'reservation/:id',
    loadComponent: () =>
      import('./public/reservations/pages/public-reservation-detail.component').then(
        (c) => c.PublicReservationDetailComponent,
      ),
  },
  {
    // Only ever reached with `skipLocationChange`, from `recoverFromStaleChunk`. Eager, since a lazy chunk is what failed
    // (docs/web-releases.md). Before the old public page URL, whose matcher would take it for a slug.
    path: LOAD_FAILED_PATH,
    component: LoadFailedComponent,
  },
  {
    // `/@:id` and `/@:id/:view` as one route, so a tab switch from the bare URL reuses the page and keeps its modules
    // alive instead of rebuilding it (docs/public-page.md, _Tabs and the dock_).
    matcher: publicPageMatcher,
    loadComponent: () =>
      import('./premises-calendar/premises-calendar.component').then((c) => c.PremisesCalendarComponent),
  },
  {
    // Before 2026-10-22 the page was `/:id`. Remove once old links are gone (docs/public-page.md, _Known follow-ups_).
    matcher: legacyPublicPageMatcher,
    redirectTo: ({ params, queryParams, fragment }) =>
      inject(Router).createUrlTree(legacyPublicPageRedirect({ id: params['id'], view: params['view'] }), {
        queryParams,
        fragment: fragment ?? undefined,
      }),
  },
  {
    // Last: anything no route above matched: URLs of three or more segments, and a bare `/@`.
    path: '**',
    loadComponent: () => import('./shared/components/not-found/not-found.component').then((c) => c.NotFoundComponent),
  },

  //   {
  //     path: 'error',
  //     loadChildren: () => import('./auth/error-page/error-page.module').then((m) => m.ErrorModule),
  //   },
]
